import { deepMerge } from '@nova/utils';

/**
 * Reserved key in config files for metadata about the config itself.
 * Should be skipped when iterating over config entries.
 */
export const CONFIG_METADATA_KEY = 'metadata';

/** Config set with items that may extend other items via the "extends" key. */
export type ExtendableConfigSet = Record<string, Record<string, unknown> & { extends?: string }>;

/** Config set after extends have been resolved (no extends key). */
export type ResolvedConfigSet = Record<string, Record<string, unknown>>;

/**
 * This will loop through all objects of a config file where keys have object values, and for all objects containing the key "extends" to a string value,
 * if the "extends" value is a key in the base config set, it will merge the given object with the that object found in the base config set.
 * @param newConfigs An object whereby at least the top layer of values are also objects, possibly having the key "extends" with a string value.
 * @param baseConfigs An object whereby at least the top layer of values are also objects.
 * @returns A new object with the same structure as the input, but with all objects that had the key "extends" merged with the base config set.
 */
export const mergeExtendedConfigSet = (newConfigs: ExtendableConfigSet, baseConfigs: ResolvedConfigSet): ResolvedConfigSet => {
  const mergedObject: ResolvedConfigSet = deepMerge({}, baseConfigs);

  for (const key in newConfigs) {
    if (!Object.prototype.hasOwnProperty.call(newConfigs, key)) { continue; }
    const newConfig = newConfigs[key];
    const baseConfigKey = newConfig.extends;
    if (baseConfigKey && typeof baseConfigKey === 'string') {
      const baseConfig = baseConfigs[baseConfigKey] ?? newConfigs[baseConfigKey];
      mergedObject[key] = deepMerge({}, baseConfig, newConfig);
      delete mergedObject[key].extends;
    }
    else {
      mergedObject[key] = deepMerge({}, newConfig);
    }
  }

  return mergedObject;
};
